import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WhatsappLogo, FloppyDisk, Warning, CheckCircle, XCircle, Copy, Plus, Trash, Star, Users, UserPlus, Crown, Plugs, Motorcycle } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { ConnectWizard } from "@/components/ConnectWizard";
import { EmbeddedSignupButton } from "@/components/EmbeddedSignupButton";
import { SecretInput } from "@/components/SecretInput";
import CouriersPanel from "@/components/CouriersPanel";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const WEBHOOK_URL = `${BACKEND}/api/whatsapp/webhook`;

function copyText(t) { navigator.clipboard.writeText(t); toast.success("Copiado"); }

function NumberCard({ n, onUpdate, onDelete, onSetDefault }) {
  const [s, setS] = useState(n);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  useEffect(() => setS(n), [n.id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/whatsapp-numbers/${n.id}`, s);
      onUpdate(data);
      toast.success("Numero actualizado");
    } catch {
      toast.error("Error al guardar");
    } finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/whatsapp-numbers/${n.id}/test`);
      setTestResult(data);
      if (data.success) toast.success("Conexion verificada con Meta");
      else toast.warning("Revisa el diagnostico de conexion");
    } catch {
      toast.error("Error al probar la conexion");
    } finally { setTesting(false); }
  };

  const configured = s.phone_number_id && s.access_token;

  return (
    <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6" data-testid={`wa-number-${n.id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-md flex items-center justify-center ${configured && s.enabled ? "bg-[rgba(16,185,129,0.12)]" : "bg-[#18181B]"}`}>
            <WhatsappLogo size={22} className={configured && s.enabled ? "text-[#10B981]" : "text-[#71717A]"} weight="fill" />
          </div>
          <div>
            <div className="font-display text-lg font-medium tracking-tight flex items-center gap-2">
              {s.label}
              {n.is_default && <span className="px-1.5 py-0.5 text-[9px] rounded bg-[rgba(94,106,210,0.15)] text-[#9EA9FF] uppercase tracking-wider font-medium flex items-center gap-1"><Star size={10} weight="fill" /> default</span>}
            </div>
            <div className="text-xs text-[#71717A] font-mono">{s.display_phone || "Sin numero visible"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!n.is_default && (
            <Button size="sm" variant="ghost" onClick={() => onSetDefault(n.id)} className="text-xs text-[#A1A1AA]" data-testid={`set-default-${n.id}`}>
              <Star size={12} className="mr-1" /> Hacer default
            </Button>
          )}
          <button onClick={() => onDelete(n.id)} className="p-1.5 rounded hover:bg-[#27272A] text-[#71717A] hover:text-[#EF4444]" data-testid={`delete-number-${n.id}`}><Trash size={14} /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Etiqueta</Label><Input value={s.label || ""} onChange={(e) => setS({ ...s, label: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1 text-sm" /></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Display Phone</Label><Input value={s.display_phone || ""} onChange={(e) => setS({ ...s, display_phone: e.target.value })} placeholder="+52 55 1234..." className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb text-xs" /></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Phone Number ID</Label><Input value={s.phone_number_id || ""} onChange={(e) => setS({ ...s, phone_number_id: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb text-xs" /></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Business Account ID</Label><Input value={s.business_account_id || ""} onChange={(e) => setS({ ...s, business_account_id: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb text-xs" /></div>
        <div className="md:col-span-2"><Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Access Token</Label><SecretInput value={s.access_token || ""} onChange={(e) => setS({ ...s, access_token: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb text-xs" testId={`access-token-${n.id}`} /></div>
        <div className="md:col-span-2"><Label className="text-[10px] uppercase tracking-wider text-[#71717A]">Verify Token (webhook)</Label><SecretInput value={s.verify_token || ""} onChange={(e) => setS({ ...s, verify_token: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb text-xs" testId={`verify-token-${n.id}`} /></div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-[#71717A]">App Secret (verificacion HMAC)</Label>
          <SecretInput value={s.app_secret || ""} onChange={(e) => setS({ ...s, app_secret: e.target.value })} placeholder="App Secret de tu app en Meta" className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb text-xs" testId={`app-secret-${n.id}`} />
          <p className="text-[10px] text-[#71717A] mt-1">Opcional pero recomendado en produccion: valida la firma X-Hub-Signature-256 de Meta y rechaza webhooks falsos. Lo encuentras en Meta App Dashboard → Configuracion → Basica.</p>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between pt-4 border-t border-[#18181B]">
        <div className="flex items-center gap-3">
          <Switch checked={s.enabled || false} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid={`enabled-${n.id}`} />
          <span className="text-xs text-[#A1A1AA]">{s.enabled ? "Activo: envia/recibe via Meta API" : "Inactivo"}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={testConnection} disabled={testing} size="sm" variant="outline" className="border-[#27272A] bg-[#18181B] text-xs" data-testid={`test-connection-${n.id}`}>
            <Plugs size={12} className="mr-1.5" /> {testing ? "Probando..." : "Probar conexion"}
          </Button>
          <Button onClick={save} disabled={saving} size="sm" className="bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid={`save-${n.id}`}>
            <FloppyDisk size={12} className="mr-1.5" weight="fill" /> {saving ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </div>
      {testResult && (
        <div className="mt-3 space-y-1.5" data-testid={`test-results-${n.id}`}>
          {testResult.checks.map((c, i) => (
            <div key={i} className="flex items-start gap-2.5 p-2 rounded-md bg-[#0A0A0C] border border-[#18181B]">
              {c.ok ? <CheckCircle size={14} weight="fill" className="text-[#10B981] mt-0.5 shrink-0" /> : <XCircle size={14} weight="fill" className="text-[#F59E0B] mt-0.5 shrink-0" />}
              <div>
                <div className="text-xs">{c.check}</div>
                <div className="text-[10px] text-[#71717A]">{c.detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [numbers, setNumbers] = useState([]);
  const [team, setTeam] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [invite, setInvite] = useState({ email: "", password: "", full_name: "", role: "agent" });
  const [tab, setTab] = useState("numbers");

  const load = async () => {
    const [n, t] = await Promise.all([api.get("/whatsapp-numbers"), api.get("/team")]);
    setNumbers(n.data);
    setTeam(t.data);
  };

  useEffect(() => { load(); }, []);

  const updateNumber = (n) => setNumbers(numbers.map((x) => x.id === n.id ? n : x));

  const deleteNumber = async (id) => {
    if (!confirm("Eliminar este numero?")) return;
    await api.delete(`/whatsapp-numbers/${id}`);
    load();
  };

  const setDefault = async (id) => {
    await api.patch(`/whatsapp-numbers/${id}`, { is_default: true });
    toast.success("Default actualizado");
    load();
  };

  const inviteMember = async () => {
    if (!invite.email || !invite.password || !invite.full_name) return toast.error("Completa todos los campos");
    try {
      await api.post("/team/invite", invite);
      toast.success(`Invitado · password: ${invite.password}`);
      setInviteOpen(false);
      setInvite({ email: "", password: "", full_name: "", role: "agent" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al invitar"); }
  };

  const removeMember = async (id) => {
    if (!confirm("Eliminar miembro del equipo?")) return;
    await api.delete(`/team/${id}`);
    toast.success("Miembro eliminado");
    load();
  };

  const isOwner = user?.role === "owner";

  const resetAgent = async () => {
    if (resetConfirm !== "RESETEAR") return toast.error('Escribe "RESETEAR" para confirmar');
    setResetting(true);
    try {
      const { data } = await api.post("/agent/reset");
      toast.success("Agente reseteado. Todo listo para configurar desde cero.");
      setResetOpen(false);
      setResetConfirm("");
      // Give backend a beat + reload to a clean state
      setTimeout(() => { window.location.href = "/app/agent"; }, 600);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al resetear");
    } finally { setResetting(false); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto fade-in">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Configuracion</div>
        <h1 className="font-display text-4xl font-medium tracking-tighter">Workspace</h1>
        <p className="text-sm text-[#A1A1AA] mt-1">Multiples numeros WhatsApp y miembros de equipo.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-transparent border-b border-[#27272A] rounded-none p-0 h-auto w-full justify-start gap-6" data-testid="settings-tabs">
          <TabsTrigger value="numbers" data-testid="tab-numbers" className="bg-transparent px-0 pb-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white text-[#71717A] data-[state=active]:border-b-2 data-[state=active]:border-[#5E6AD2]">
            <WhatsappLogo size={14} className="mr-2" weight="fill" /> Numeros WhatsApp
          </TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team" className="bg-transparent px-0 pb-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white text-[#71717A] data-[state=active]:border-b-2 data-[state=active]:border-[#5E6AD2]">
            <Users size={14} className="mr-2" /> Equipo
          </TabsTrigger>
          <TabsTrigger value="couriers" data-testid="tab-couriers" className="bg-transparent px-0 pb-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white text-[#71717A] data-[state=active]:border-b-2 data-[state=active]:border-[#5E6AD2]">
            <Motorcycle size={14} className="mr-2" weight="fill" /> Domiciliarios
          </TabsTrigger>
          <TabsTrigger value="webhook" data-testid="tab-webhook" className="bg-transparent px-0 pb-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white text-[#71717A] data-[state=active]:border-b-2 data-[state=active]:border-[#5E6AD2]">
            Webhook
          </TabsTrigger>
        </TabsList>

        <TabsContent value="numbers" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-[#A1A1AA]">{numbers.length} numero(s) registrado(s)</div>
            <div className="flex items-center gap-2">
              <EmbeddedSignupButton onConnected={load} />
              <Button onClick={() => setAddOpen(true)} variant="outline" className="border-[#27272A] bg-[#18181B]" data-testid="add-number-button">
                <Plus size={14} className="mr-1.5" weight="bold" /> Conexion manual
              </Button>
            </div>
            <ConnectWizard open={addOpen} onOpenChange={setAddOpen} onCreated={load} />
          </div>
          {numbers.length === 0 && (
            <div className="border border-dashed border-[#27272A] rounded-lg p-12 text-center text-[#71717A] text-sm">
              <WhatsappLogo size={32} className="mx-auto mb-3 text-[#3F3F46]" />
              Sin numeros. Agrega el primero para activar la integracion Meta.
            </div>
          )}
          {numbers.map((n) => (
            <NumberCard key={n.id} n={n} onUpdate={updateNumber} onDelete={deleteNumber} onSetDefault={setDefault} />
          ))}
        </TabsContent>

        <TabsContent value="team" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-[#A1A1AA]">{team.length} miembro(s) en el workspace</div>
            {isOwner && (
              <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="invite-member-button"><UserPlus size={14} className="mr-1.5" /> Invitar miembro</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121214] border-[#27272A] text-white">
                  <DialogHeader><DialogTitle className="font-display tracking-tight">Invitar al equipo</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Nombre completo</Label><Input value={invite.full_name} onChange={(e) => setInvite({ ...invite, full_name: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="invite-name" /></div>
                    <div><Label>Email</Label><Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="invite-email" /></div>
                    <div><Label>Password inicial</Label><Input value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb text-xs" data-testid="invite-password" /></div>
                    <div>
                      <Label>Rol</Label>
                      <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                        <SelectTrigger className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="invite-role"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#121214] border-[#27272A]">
                          <SelectItem value="admin">Admin (gestiona todo)</SelectItem>
                          <SelectItem value="agent">Agent (responde y vende)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-[#71717A]">Compartele al miembro: email + password. Podra iniciar sesion con esos datos.</p>
                    <Button onClick={inviteMember} className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="save-invite-button">Crear acceso</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="border border-[#27272A] bg-[#121214] rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="team-table">
              <thead className="border-b border-[#27272A] text-[10px] uppercase tracking-wider text-[#71717A]">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Miembro</th>
                  <th className="text-left px-5 py-3 font-medium">Email</th>
                  <th className="text-left px-5 py-3 font-medium">Rol</th>
                  <th className="text-left px-5 py-3 font-medium">Creado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {team.map((m) => (
                  <tr key={m.id} className="border-b border-[#18181B] hover:bg-[#0E0E10]">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#5E6AD2] to-[#3B82F6] flex items-center justify-center text-xs">{m.full_name[0]?.toUpperCase()}</div>
                        <span className="font-medium">{m.full_name}</span>
                        {m.is_owner && <Crown size={12} className="text-[#F59E0B]" weight="fill" />}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-[#A1A1AA] text-xs">{m.email}</td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-0.5 text-[10px] rounded uppercase tracking-wider font-medium bg-[rgba(94,106,210,0.12)] text-[#9EA9FF]">{m.role}</span>
                    </td>
                    <td className="px-5 py-3 text-xs text-[#71717A] font-mono">{new Date(m.created_at).toLocaleDateString("es-MX")}</td>
                    <td className="px-5 py-3 text-right">
                      {!m.is_owner && isOwner && (
                        <button onClick={() => removeMember(m.id)} className="p-1.5 rounded hover:bg-[#27272A] text-[#71717A] hover:text-[#EF4444]" data-testid={`remove-member-${m.id}`}><Trash size={14} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="couriers" className="mt-6">
          <CouriersPanel />
        </TabsContent>

        <TabsContent value="webhook" className="mt-6">
          <div className="border border-[#27272A] bg-[#121214] rounded-lg p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Webhook</div>
            <h3 className="font-display text-xl font-medium tracking-tight mb-2">URL unica para todos tus numeros</h3>
            <p className="text-xs text-[#A1A1AA] mb-4">Configura esta URL en tu app Meta (Webhooks → WhatsApp) y usa el Verify Token de cada numero.</p>
            <div className="flex gap-2 mt-1">
              <Input readOnly value={WEBHOOK_URL} className="bg-[#0A0A0C] border-[#27272A] font-mono-jb text-xs" data-testid="webhook-url-input" />
              <Button onClick={() => copyText(WEBHOOK_URL)} size="sm" variant="outline" className="border-[#27272A] bg-[#18181B]" data-testid="copy-webhook-url"><Copy size={14} /></Button>
            </div>
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Como configurar</div>
              <ol className="text-sm text-[#A1A1AA] space-y-2 list-decimal pl-5">
                <li>En <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" className="text-[#9EA9FF] hover:underline">developers.facebook.com</a> abre tu app → WhatsApp → Configuration → Webhooks.</li>
                <li>Edita Callback URL y pega la URL de arriba.</li>
                <li>Pega el Verify Token que definiste en uno de tus numeros y haz "Verify and save".</li>
                <li>Suscribete a los campos: messages, message_status, message_template_status_update.</li>
              </ol>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {isOwner && (
        <div className="mt-10 border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.05)] rounded-lg p-6" data-testid="reset-agent-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#EF4444] mb-1">Zona de riesgo</div>
              <h3 className="font-display text-xl font-medium tracking-tight mb-1">Resetear Agente</h3>
              <p className="text-sm text-[#A1A1AA] max-w-xl">
                Borra <b>todo</b> lo que aprendió el agente: prompt y configuración, base de conocimiento cargada, contactos, historial de conversaciones, pedidos, comprobantes, citas y campañas. Deja intactos: usuarios del equipo, números de WhatsApp y domiciliarios. <b>Esta acción no se puede deshacer.</b>
              </p>
            </div>
            <Button
              onClick={() => setResetOpen(true)}
              className="bg-[#EF4444] hover:bg-[#DC2626] text-white shrink-0"
              data-testid="reset-agent-button"
            >
              <Warning size={14} className="mr-1.5" weight="fill" /> Resetear Agente
            </Button>
          </div>
        </div>
      )}

      <Dialog open={resetOpen} onOpenChange={(v) => { setResetOpen(v); if (!v) setResetConfirm(""); }}>
        <DialogContent className="bg-[#121214] border-[#EF4444]/40 text-white">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-[#EF4444]">
              <Warning size={20} weight="fill" /> Confirmar reseteo del agente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[#A1A1AA]">
              Vas a borrar de forma <b className="text-white">permanente</b>:
              <ul className="mt-2 ml-5 list-disc space-y-1 text-xs">
                <li>El prompt y toda la configuración del agente IA</li>
                <li>La base de conocimiento cargada (documentos y chunks)</li>
                <li>Todos los contactos y el historial de conversaciones</li>
                <li>Pedidos, comprobantes de pago, citas y campañas</li>
              </ul>
              <div className="mt-3 text-[11px] text-[#71717A]">
                No se tocan: usuarios del equipo, números de WhatsApp conectados y domiciliarios.
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#71717A] mb-1.5 block">
                Para confirmar, escribe <span className="font-mono text-[#EF4444]">RESETEAR</span>
              </Label>
              <Input
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                placeholder="RESETEAR"
                className="bg-[#0A0A0C] border-[#27272A] font-mono-jb"
                data-testid="reset-agent-confirm-input"
                autoFocus
              />
            </div>
            <Button
              onClick={resetAgent}
              disabled={resetting || resetConfirm !== "RESETEAR"}
              className="w-full bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="reset-agent-confirm-button"
            >
              {resetting ? "Reseteando..." : "Sí, borrar todo el agente"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
